"use client"

import { useCallback, useMemo, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { SlidersHorizontal, X } from "lucide-react"
import { useCatalogTransition } from "@/components/public/CatalogTransitionContext"
import { useTranslations } from "@/lib/i18n/client"
import { cn, FILTER_PENDING_CLASSES } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  CatalogFiltersPanel,
  YEAR_MIN,
  YEAR_MAX,
} from "@/components/public/CatalogFiltersPanel"
import type { I18nText } from "@/lib/types"

interface CategoriaOption {
  slug: string
  nome: I18nText
}

interface FabricanteOption {
  slug: string
  nome: string
}

interface ModeloOption {
  slug: string
  nome: I18nText
  fabricante_slug: string
  fabricante_nome: string
  categoria_slug: string
}

interface CatalogFiltersSheetProps {
  categorias: CategoriaOption[]
  fabricantes: FabricanteOption[]
  modelos: ModeloOption[]
  localizacoes: string[]
  isHelicopteros: boolean
}

function parseList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

function countActive(p: URLSearchParams): number {
  let count = 0
  if (p.get("q")) count++
  count += parseList(p.get("categoria") ?? "").length
  count += parseList(p.get("fabricante") ?? "").length
  count += parseList(p.get("modelo") ?? "").length
  count += parseList(p.get("localizacao") ?? "").length
  if (p.get("destaque") === "1") count++
  if (p.get("com_fotos") === "1") count++
  const ymin = Number(p.get("ano_min")) || YEAR_MIN
  const ymax = Number(p.get("ano_max")) || YEAR_MAX
  if (ymin > YEAR_MIN || ymax < YEAR_MAX) count++
  return count
}

export function CatalogFiltersSheet(props: CatalogFiltersSheetProps) {
  const { t } = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { isPending, startTransition } = useCatalogTransition()

  const [open, setOpen] = useState(false)
  const [stagedParams, setStagedParams] = useState<URLSearchParams>(
    () => new URLSearchParams(searchParams.toString()),
  )

  const appliedActive = useMemo(
    () => countActive(new URLSearchParams(searchParams.toString())),
    [searchParams],
  )
  const stagedActive = useMemo(() => countActive(stagedParams), [stagedParams])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        const fresh = new URLSearchParams(searchParams.toString())
        fresh.delete("pagina")
        setStagedParams(fresh)
      }
      setOpen(next)
    },
    [searchParams],
  )

  const handleApply = useCallback(() => {
    const qs = stagedParams.toString()
    setOpen(false)
    startTransition(() => {
      router.push(qs ? `/aeronaves?${qs}` : "/aeronaves")
    })
  }, [router, startTransition, stagedParams])

  return (
    <div
      aria-busy={isPending}
      className={cn("mb-4 md:hidden", isPending && FILTER_PENDING_CLASSES)}
    >
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full">
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            {t.catalog.filters}
            {appliedActive > 0 && (
              <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-navy-800 px-1.5 text-[10px] font-medium text-white">
                {appliedActive}
              </span>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          className="flex max-h-[85dvh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        >
          <header className="flex shrink-0 flex-row items-center justify-between border-b-[0.5px] border-slate-200 bg-white px-4 py-3">
            <DialogTitle>{t.catalog.filters}</DialogTitle>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.common.close}
              className="-mr-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-700 transition-colors hover:bg-slate-100"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4">
            <CatalogFiltersPanel
              {...props}
              mode="staged"
              stagedParams={stagedParams}
              onStagedChange={setStagedParams}
            />
          </div>

          <div className="flex shrink-0 items-center gap-3 border-t-[0.5px] border-slate-200 bg-white px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3">
            <Button
              variant="outline"
              className="h-11 flex-1"
              onClick={() => setStagedParams(new URLSearchParams())}
            >
              {t.catalog.clear}
            </Button>
            <Button
              className="h-11 flex-1 bg-navy-800 hover:bg-navy-900"
              onClick={handleApply}
            >
              {stagedActive > 0
                ? t.catalog.applyWithCount.replace(
                    "{count}",
                    String(stagedActive),
                  )
                : t.catalog.apply}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
